import React, { useState } from "react";
import { Sparkles, Loader2, Megaphone, Share2, ImagePlus, Copy, Check, Wand2, RefreshCw, Upload, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { studioGenerate, uploadFile } from "../../api";
import { useToast } from "../../hooks/use-toast";
import AiAssist from "../AiAssist";

const input = "w-full h-[46px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";
const area = "w-full px-4 py-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] resize-none";

const SOCIAL_PLATFORMS = [["instagram", "Instagram"], ["facebook", "Facebook"], ["linkedin", "LinkedIn"], ["twitter", "X / Twitter"]];
const AD_PLATFORMS = [["meta", "Meta (FB + IG)"], ["facebook", "Facebook"], ["instagram", "Instagram"], ["google", "Google Ads"]];
const TONES = ["Warm & friendly", "Professional", "Luxury", "Playful", "Urgent", "Informative"];

const ContentStudio = ({ properties = [], onSaved }) => {
  const { toast } = useToast();
  const [channel, setChannel] = useState("social");
  const [platform, setPlatform] = useState("instagram");
  const [brief, setBrief] = useState("");
  const [propertyId, setPropertyId] = useState("none");
  const [tone, setTone] = useState("");
  const [genImage, setGenImage] = useState(true);
  const [imageStyle, setImageStyle] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);

  const platforms = channel === "ad" ? AD_PLATFORMS : SOCIAL_PLATFORMS;
  const switchChannel = (c) => { setChannel(c); setPlatform(c === "ad" ? "meta" : "instagram"); };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map((f) => uploadFile(f)));
      setUploads((u) => [...u, ...urls]);
    } catch { toast({ title: "Image upload failed", description: "Please try a smaller image." }); }
    setUploading(false);
    e.target.value = "";
  };
  const removeUpload = (url) => setUploads((u) => u.filter((x) => x !== url));

  const run = async () => {
    if (!brief.trim() && propertyId === "none") { toast({ title: "Add a brief or pick a property first" }); return; }
    setBusy(true); setResult(null);
    try {
      const r = await studioGenerate({
        channel, platform, brief: brief.trim(),
        propertyId: propertyId === "none" ? null : propertyId,
        tone, generateImage: genImage, imageStyle,
        uploadedImages: uploads,
      });
      setResult(r);
      toast({ title: "Content created", description: r.imageFailed ? "Copy ready — image generation was unavailable." : "Saved to Pending Review." });
      onSaved && onSaved();
    } catch (e) {
      toast({ title: "Generation failed", description: e?.response?.data?.detail || "Please try again." });
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result?.content || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast({ title: "Couldn't copy", description: "Please select and copy the text manually." }); }
  };
  const s = result?.structured || {};

  return (
    <div className="grid lg:grid-cols-[420px_1fr] gap-8" data-testid="content-studio">
      {/* Composer */}
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6 h-fit">
        <h3 className="font-display text-[22px] font-400 text-[#2b2b2b] mb-1 flex items-center gap-2"><Wand2 size={20} className="text-[#b8935f]" /> Content Studio</h3>
        <p className="text-[13px] text-[#8a8a8a] mb-5">The AI writes the copy and creates or picks the imagery for you.</p>

        <div className="flex gap-2 mb-4">
          <button onClick={() => switchChannel("social")} data-testid="studio-channel-social" className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-500 transition-colors ${channel === "social" ? "bg-[#1c1c1e] text-white" : "bg-[#faf7f2] text-[#6a6a6a] border border-[#f0ece5]"}`}><Share2 size={15} /> Social post</button>
          <button onClick={() => switchChannel("ad")} data-testid="studio-channel-ad" className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-[13px] font-500 transition-colors ${channel === "ad" ? "bg-[#1c1c1e] text-white" : "bg-[#faf7f2] text-[#6a6a6a] border border-[#f0ece5]"}`}><Megaphone size={15} /> Advert</button>
        </div>

        <label className="text-[12px] text-[#6a6a6a] mb-1 block">Platform</label>
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger className="h-[46px] border-[#e2d8c8] mb-4" data-testid="studio-platform"><SelectValue /></SelectTrigger>
          <SelectContent>{platforms.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>

        <label className="text-[12px] text-[#6a6a6a] mb-1 block">Feature a listing (optional)</label>
        <Select value={propertyId} onValueChange={setPropertyId}>
          <SelectTrigger className="h-[46px] border-[#e2d8c8] mb-4" data-testid="studio-property"><SelectValue placeholder="None" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No specific listing</SelectItem>
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
          </SelectContent>
        </Select>

        <label className="text-[12px] text-[#6a6a6a] mb-1 block">What's it about?</label>
        <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={3} placeholder="e.g. Promote our new 3-bed in Widcombe, open house this Saturday" className={area + " mb-3"} data-testid="studio-brief" />

        <label className="text-[12px] text-[#6a6a6a] mb-1 block">Tone</label>
        <Select value={tone} onValueChange={setTone}>
          <SelectTrigger className="h-[46px] border-[#e2d8c8] mb-4" data-testid="studio-tone"><SelectValue placeholder="Brand default" /></SelectTrigger>
          <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>

        <label className="flex items-center gap-2 mb-3 cursor-pointer">
          <input type="checkbox" checked={genImage} onChange={(e) => setGenImage(e.target.checked)} className="w-4 h-4 accent-[#b8935f]" data-testid="studio-genimage" />
          <span className="text-[13px] text-[#3a3a3a] flex items-center gap-1.5"><ImagePlus size={15} className="text-[#b8935f]" /> Let AI create an image</span>
        </label>
        {genImage && <input value={imageStyle} onChange={(e) => setImageStyle(e.target.value)} placeholder="Image style (optional) — e.g. bright, lifestyle, dusk" className={input + " mb-4"} data-testid="studio-imagestyle" />}

        {/* Upload your own images */}
        <div className="mb-4">
          <label htmlFor="studio-upload-input" className="flex items-center justify-center gap-2 h-[46px] rounded-lg border border-dashed border-[#e2d8c8] text-[13px] text-[#6a6a6a] cursor-pointer hover:border-[#b8935f] hover:text-[#b8935f] transition-colors" data-testid="studio-upload-label">
            {uploading ? <><Loader2 size={15} className="animate-spin" /> Uploading…</> : <><Upload size={15} /> Add your own images</>}
          </label>
          <input id="studio-upload-input" type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="studio-upload-input" />
          {uploads.length > 0 && (
            <div className="flex gap-2 flex-wrap mt-2" data-testid="studio-uploaded-images">
              {uploads.map((url) => (
                <div key={url} className="relative w-16 h-16 rounded-lg overflow-hidden border border-[#f0ece5] group">
                  <img src={url} alt="upload" className="w-full h-full object-cover" />
                  <button onClick={() => removeUpload(url)} data-testid="studio-remove-upload" className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button onClick={run} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 h-[50px] rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60" data-testid="studio-generate">
          {busy ? <><Loader2 size={17} className="animate-spin" /> Creating content{genImage ? " & imagery…" : "…"}</> : <><Sparkles size={17} /> Generate</>}
        </button>
        {busy && genImage && <p className="text-[11px] text-[#a0a0a0] text-center mt-2">Image generation can take up to a minute.</p>}
      </div>

      {/* Result */}
      <div>
        {!result && !busy && (
          <div className="h-full min-h-[360px] rounded-2xl border border-dashed border-[#e2d8c8] bg-white/60 flex flex-col items-center justify-center text-center p-10">
            <Wand2 size={30} className="text-[#d9c4a6] mb-4" />
            <h4 className="font-display text-[20px] text-[#2b2b2b] mb-1">Your content will appear here</h4>
            <p className="text-[13px] text-[#8a8a8a] max-w-[360px]">Pick social or advert, add a brief, and the AI will write the copy and produce imagery — ready to review, tweak and publish.</p>
          </div>
        )}
        {busy && (
          <div className="h-full min-h-[360px] rounded-2xl border border-[#f0ece5] bg-white flex flex-col items-center justify-center text-center p-10">
            <Loader2 size={30} className="text-[#b8935f] animate-spin mb-4" />
            <p className="text-[14px] text-[#6a6a6a]">Crafting on-brand {channel === "ad" ? "advert" : "post"}{genImage ? " and imagery" : ""}…</p>
          </div>
        )}
        {result && (
          <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="studio-result">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[11px] uppercase tracking-wide text-[#b8935f] font-500">{channel === "ad" ? "Advert" : "Social post"} · {platform}</span>
              <span className="text-[11px] px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">Saved to Pending Review</span>
            </div>

            {(result.imageSuggestions || []).length > 0 && (
              <div className="mb-5">
                <p className="text-[12px] text-[#6a6a6a] mb-2">{result.generatedImage ? "AI-created image + listing photos" : "Suggested images"}</p>
                <div className="flex gap-2 overflow-x-auto pb-1" data-testid="studio-images">
                  {result.imageSuggestions.map((img, i) => (
                    <img key={i} src={img} alt={`option ${i + 1}`} className={`h-28 w-28 object-cover rounded-lg border shrink-0 ${i === 0 ? "border-[#b8935f] border-2" : "border-[#f0ece5]"}`} />
                  ))}
                </div>
                {result.imageFailed && <p className="text-[11px] text-red-500 mt-1">AI image couldn't be created this time — the copy is ready to use.</p>}
              </div>
            )}

            {channel === "ad" ? (
              <div className="space-y-3">
                <Field label="Headline" value={s.headline} />
                <div>
                  <p className="text-[12px] text-[#9a9a9a] mb-1 flex items-center justify-between">Primary text <AiAssist channel="ad" value={result.content} onChange={(v) => setResult({ ...result, content: v })} testid="studio-ai-primary" /></p>
                  <p className="text-[14px] text-[#3a3a3a] whitespace-pre-wrap leading-relaxed">{result.content}</p>
                </div>
                <Field label="Description" value={s.description} />
                <div className="flex flex-wrap gap-4">
                  <Field label="Call to action" value={s.cta} inline />
                  <Field label="Suggested audience" value={s.audience} inline />
                </div>
              </div>
            ) : (
              <div>
                {s.hook && <p className="text-[13px] font-500 text-[#2b2b2b] mb-2">{s.hook}</p>}
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[12px] text-[#9a9a9a]">Caption</p>
                  <AiAssist channel="social" value={result.content} onChange={(v) => setResult({ ...result, content: v })} testid="studio-ai-caption" />
                </div>
                <p className="text-[14px] text-[#3a3a3a] whitespace-pre-wrap leading-relaxed mb-3">{result.content}</p>
                {(result.hashtags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {result.hashtags.map((h) => <span key={h} className="text-[12px] px-2 py-0.5 rounded-full bg-[#faf7f2] border border-[#f0ece5] text-[#6a6a6a]">{h}</span>)}
                  </div>
                )}
                {s.firstComment && <p className="text-[12px] text-[#8a8a8a] border-l-2 border-[#e2d8c8] pl-3">First comment: {s.firstComment}</p>}
              </div>
            )}

            <div className="flex items-center gap-2 mt-6 pt-4 border-t border-[#f0ece5]">
              <button onClick={copy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#e2d8c8] text-[13px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors" data-testid="studio-copy">{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}</button>
              <button onClick={run} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#e2d8c8] text-[13px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors" data-testid="studio-regenerate"><RefreshCw size={14} /> Regenerate</button>
              <span className="text-[12px] text-[#a0a0a0] ml-auto">Find it in Pending Review to approve &amp; publish.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, inline }) => (
  <div className={inline ? "" : "w-full"}>
    <p className="text-[12px] text-[#9a9a9a] mb-0.5">{label}</p>
    <p className="text-[14px] text-[#2b2b2b] font-500">{value || "—"}</p>
  </div>
);

export default ContentStudio;
